/**
 * 🔴 **`localStorage`·`sessionStorage` 는 없을 수도 있고, 접근 자체가 던질 수도 있다.**
 *
 * 실측 사고 2026-09-28 (`ENTER-FUN-STREAMER-C`, production, 실사용자 2건):
 * ```
 * React ErrorBoundary TypeError: null is not an object (evaluating 'localStorage.getItem')
 *   at lib/auth.ts getAccessToken
 *   at contexts/AuthContext.tsx (mount)
 * browser=Chrome Mobile iOS 143.0.7499 · os=iOS 26.6.2 · device=iPhone
 * url=https://streamer.yoy.live/oauth/postforme/done
 * ```
 * 즉 **`localStorage` 자체가 `null`** 이었다(iOS WebKit 의 일부 개인정보·임베드 문맥).
 * 그 크래시가 ErrorBoundary 까지 올라가서, 스트리머는 OAuth 연동을 **마치는 화면에서**
 * 오류 화면을 봤다 — 전환 경로 한복판이다.
 *
 * ⚠️ 두 실패 모양을 **둘 다** 막아야 한다:
 *   ① `localStorage === null`  → 속성 접근에서 TypeError
 *   ② 접근이 예외를 던진다      → Safari 시크릿·쿠키 차단 환경
 *
 * 🔴 **왜 `packages/ui` 에 있나 — 같은 코드가 다섯 앱에 흩어져 있었다.**
 *    부팅 경로(`lib/auth.ts` 의 `getAccessToken`)는 streamer·sponsor·admin·editor·landing
 *    **전부** 같은 모양이었다. 즉 그 iOS 문맥은 다섯 앱을 똑같이 죽인다. streamer 만
 *    고치는 것은 「증상이 보고된 곳만 고치는 것」이고, 이 레포는 그 실패를
 *    (`vpdeath.py` 사건) 이미 문서에 적어 뒀다.
 *
 *    ⚠️ **`apps/landing` 은 이 모듈을 쓰지 않는다.** `@repo/ui` 를 의존하지 않는 유일한
 *       앱이고(react-router SSR · Node 22), 그 한 함수를 위해 React 컴포넌트 패키지를
 *       의존성에 넣는 것은 빌드 위험이 더 크다. 그래서 `apps/landing/src/lib/fan-session.ts`
 *       가 같은 가드를 **자체적으로** 갖고, 주석이 이 파일을 가리킨다.
 *       두 구현이 어긋나지 않게 `tests/storage-guard.test.ts` 가 전 앱을 훑는다.
 *
 *    ℹ️ `apps/streamer/src/lib/{referral,attribution}-storage.ts` 도 자체 `safeSession()`
 *       을 갖고 있다. 이미 정확하고 프로덕션에서 검증된 경로라 건드리지 않았다 —
 *       위 검사가 그 둘을 «가드를 가진 파일» 로 인정한다.
 *
 * 🔴 **조용히 실패하지만 «값이 없다» 와 구별되지는 않는다.** 그건 의도다 — 토큰을 못 읽으면
 *    로그인이 안 된 것과 같은 상태이고, 그게 크래시보다 낫다. 대신 그 사실을 삼키지 않으려고
 *    {@link storageAvailable} 를 노출한다(호출부가 원하면 안내를 띄울 수 있다).
 */

/** 저장소 종류. 기본은 `local` — 토큰·설정이 거기 있다. */
export type StorageArea = 'local' | 'session';

/**
 * 저장소를 **값으로** 받아 확인한다.
 *
 * ⚠️ `globalThis.localStorage?.getItem(...)` 처럼 옵셔널 체이닝만 쓰면 부족하다. 그건
 *    `undefined` 는 막지만 **`null` 도 막는다** — 여기까지는 맞다. 그러나 「접근 자체가
 *    던지는」 ②를 못 막는다. 그래서 `try` 안에서 «읽어 두고» 확인한다.
 */
function store(area: StorageArea): Storage | null {
  try {
    const s: Storage | null =
      (area === 'local' ? globalThis.localStorage : globalThis.sessionStorage) ?? null;
    return s;
  } catch {
    return null;
  }
}

/** 이 브라우저에서 저장소를 쓸 수 있나. 호출부가 안내 문구를 고르는 데 쓴다. */
export function storageAvailable(area: StorageArea = 'local'): boolean {
  return store(area) !== null;
}

/**
 * `Storage` 객체가 직접 필요한 자리용(주입받는 헬퍼에 넘길 때).
 * ⚠️ 돌려받은 값에 **`?.` 를 쓰거나 null 검사를 해라.** 그냥 쓰면 이 모듈의 존재 이유가 없다.
 */
export function safeStore(area: StorageArea = 'local'): Storage | null {
  return store(area);
}

export function safeGet(key: string, area: StorageArea = 'local'): string | null {
  try {
    return store(area)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

/** 저장 성공 여부를 돌려준다 — 실패를 «했다» 로 기록하지 않기 위해서다. */
export function safeSet(key: string, value: string, area: StorageArea = 'local'): boolean {
  try {
    const s = store(area);
    if (!s) return false;
    s.setItem(key, value);
    return true;
  } catch {
    // 용량 초과(QuotaExceededError)도 여기로 온다.
    return false;
  }
}

export function safeRemove(key: string, area: StorageArea = 'local'): void {
  try {
    store(area)?.removeItem(key);
  } catch {
    /* 지우지 못한 것은 되돌릴 방법이 없다 — 흐름을 깨뜨리지 않는다 */
  }
}
